import { makeStyles } from '@material-ui/core'
import { RetentionReferenceStatuses } from 'components/Patients/TreatmentTracker/txTrackerConsts'
import useTxTracker from 'components/Patients/TreatmentTracker/useTxTracker'
import { TreatmentTypes } from 'consts/treatmentConsts'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import usePreviouslyUploadedStls from './usePreviouslyUploadedStls'
import { logError } from 'utils/logUtils'

const useStyles = makeStyles({
  uploadedSTLs: {
    background: '#F2F3F5',
    height: '100%',
    marginTop: 10,
    borderRadius: 10,
    padding: 20,
    overflowY: 'auto'
  },
  setLabel: {
    fontWeight: 600,
    lineHeight: '30px'
  },
  stlsRange: {
    lineHeight: '30px'
  },
  section: {
    '&:not(:first-child)': {
      marginTop: 10
    }
  }
})

/**
 * The "Uploaded STLs" panel for practices with the Retention 3D Tx Tracker: lists every set
 * the patient has (Retention reference and active treatment plan), even when it belongs to
 * another status. The "doesn't have ... yet" empty copy is shown only for the set of the
 * patient's current status.
 */
const RetentionAwareUploadedStls = ({ stls, treatmentType, provider, txTrackerRetentionStls, isRetentionStatus }) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const { calculateTreatmentType } = useTxTracker()
  const { generateAlignersUploadedStls, generateBracesUploadedStls } = usePreviouslyUploadedStls()

  const activeReference = useMemo(() => {
    try {
      const { activeReferenceId, references } = JSON.parse(txTrackerRetentionStls || '{}')
      return references?.[activeReferenceId] || null
    } catch (ex) {
      // Returning null here reads on screen as "this patient has no reference", which is the
      // one thing that would make a doctor upload a second set on top of a working one.
      logError('RetentionAwareUploadedStls: could not read the retention reference stack', { ex })
      return null
    }
  }, [txTrackerRetentionStls])

  const calculatedTreatmentType = calculateTreatmentType({ treatmentType, provider })

  const generateArchStls = archStls =>
    calculatedTreatmentType === TreatmentTypes.Aligners
      ? generateAlignersUploadedStls(archStls)
      : generateBracesUploadedStls(archStls)

  const upperLabel = t('dialogs.patientInfo.txTrackerSetup.upperLabel')
  const lowerLabel = t('dialogs.patientInfo.txTrackerSetup.lowerLabel')

  let retentionBody = null
  if (activeReference?.status === RetentionReferenceStatuses.Ready) {
    // A Retention reference is one final model per arch.
    retentionBody = (
      <>
        <div className={classes.stlsRange}>
          {upperLabel}: {t('dialogs.patientInfo.txTrackerSetup.lastBracesStl')}
        </div>
        <div className={classes.stlsRange}>
          {lowerLabel}: {t('dialogs.patientInfo.txTrackerSetup.lastBracesStl')}
        </div>
      </>
    )
  } else if (activeReference?.status === RetentionReferenceStatuses.Pending) {
    retentionBody = (
      <div className={classes.stlsRange}>{t('dialogs.patientInfo.txTrackerSetup.retentionReferencePending')}</div>
    )
  } else if (activeReference?.status === RetentionReferenceStatuses.Failed) {
    retentionBody = (
      <div className={classes.stlsRange}>{t('dialogs.patientInfo.txTrackerSetup.retentionReferenceFailed')}</div>
    )
  } else if (activeReference) {
    // A reference in a state this build has no copy for. It still exists, so the empty-state
    // message below would be a lie that invites a duplicate upload.
    retentionBody = (
      <div className={classes.stlsRange}>{t('dialogs.patientInfo.txTrackerSetup.retentionReferenceUnknownState')}</div>
    )
  } else if (isRetentionStatus) {
    retentionBody = <div>{t('dialogs.patientInfo.txTrackerSetup.noRetentionStls')}</div>
  }

  let activeBody = null
  if (stls) {
    activeBody = (
      <>
        <div className={classes.stlsRange}>
          {upperLabel}: {generateArchStls(stls.upper)}
        </div>
        <div className={classes.stlsRange}>
          {lowerLabel}: {generateArchStls(stls.lower)}
        </div>
      </>
    )
  } else if (!isRetentionStatus) {
    activeBody = <div>{t('dialogs.patientInfo.txTrackerSetup.noActiveStls')}</div>
  }

  const sections = [
    retentionBody && {
      key: 'retention',
      label: t('dialogs.patientInfo.txTrackerSetup.retentionStlsLabel'),
      body: retentionBody
    },
    activeBody && {
      key: 'active',
      label: t('dialogs.patientInfo.txTrackerSetup.activeTreatmentStlsLabel'),
      body: activeBody
    }
  ].filter(Boolean)

  // The current status's set is listed first.
  if (!isRetentionStatus) {
    sections.reverse()
  }

  return (
    <div className={classes.uploadedSTLs}>
      {sections.map(section => (
        <div key={section.key} className={classes.section}>
          <div className={classes.setLabel}>{section.label}</div>
          {section.body}
        </div>
      ))}
    </div>
  )
}

export default RetentionAwareUploadedStls
